/**
 * Variants — the SKU-bearing thing that actually has stock and a price. `variant.create` and
 * `variant.edit` gate the two writes; reads share `product.view`.
 *
 * `createVariant` never fails on a near-duplicate by itself — the server returns
 * `{ variant: null, nearMatches: [...] }` (200, not an error) when it finds candidates and
 * `confirmDespiteMatches` was not set, so the caller decides. Sending `confirmDespiteMatches: true`
 * on the next call is how "create anyway" actually creates it. An exact fingerprint match is
 * still refused outright by the database regardless of that flag — that one is never a choice.
 */

import { api } from '../api.js';
import { isPresent } from '../../lib/nullish.js';
import { stockListTag, variantListTag, variantTag } from '../tags.js';
import type {
  AdminVariant,
  CategoryId,
  Paged,
  ProductId,
  ShopId,
  VariantCreationResult,
  VariantId,
  VariantMatch,
} from '../../types/api.js';

export type ListVariantsParams = {
  readonly term?: string | undefined;
  readonly categoryId?: CategoryId | undefined;
  readonly productId?: ProductId | undefined;
  readonly brand?: string | undefined;
  readonly shopId?: ShopId | undefined;
  readonly includeInactive?: boolean | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export type CheckDuplicatesBody = {
  readonly productId: ProductId;
  readonly attributes: Readonly<Record<string, string | null>>;
  readonly excludingVariantId?: VariantId | undefined;
};

export type OpeningStockLine = {
  readonly shopId: ShopId;
  readonly quantity: number;
  readonly unitCostKes: number;
  /** Required, one per unit, when the product is serialized — there is no later screen to fill these in. */
  readonly serials?: readonly string[] | undefined;
};

export type CreateVariantBody = {
  readonly productId: ProductId;
  readonly attributes: Readonly<Record<string, string | null>>;
  readonly barcode?: string | undefined;
  readonly lowStockThreshold: number;
  readonly confirmDespiteMatches: boolean;
  readonly openingStock?: readonly OpeningStockLine[] | undefined;
};

export type UpdateVariantBody = {
  readonly attributes: Readonly<Record<string, string | null>>;
  readonly barcode?: string | undefined;
  readonly lowStockThreshold: number;
};

export const variantsApi = api.injectEndpoints({
  endpoints: (build) => ({
    listVariants: build.query<Paged<AdminVariant>, ListVariantsParams>({
      query: (params) => ({ url: '/variants', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: (result) => [
        variantListTag(),
        ...(result?.items ?? []).map((variant) => variantTag(variant.id)),
      ],
    }),

    checkVariantDuplicates: build.query<VariantMatch[], CheckDuplicatesBody>({
      query: (body) => ({ url: '/variants/duplicate-check', method: 'POST', body }),
    }),

    createVariant: build.mutation<VariantCreationResult, CreateVariantBody>({
      query: (body) => ({ url: '/variants', method: 'POST', body }),
      invalidatesTags: (result, _error, body) =>
        isPresent(result?.variant)
          ? [variantListTag(), ...((body.openingStock?.length ?? 0) > 0 ? [stockListTag()] : [])]
          : [],
    }),

    updateVariant: build.mutation<AdminVariant, { id: VariantId; body: UpdateVariantBody }>({
      query: ({ id, body }) => ({ url: `/variants/${id}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [variantTag(id), variantListTag()],
    }),

    setVariantActive: build.mutation<void, { id: VariantId; isActive: boolean }>({
      query: ({ id, isActive }) => ({
        url: `/variants/${id}/activation`,
        method: 'POST',
        body: { isActive },
      }),
      invalidatesTags: (_result, _error, { id }) => [variantTag(id), variantListTag()],
    }),
  }),
});

export const {
  useListVariantsQuery,
  useLazyCheckVariantDuplicatesQuery,
  useCreateVariantMutation,
  useUpdateVariantMutation,
  useSetVariantActiveMutation,
} = variantsApi;
